import React from 'react';
import { motion } from 'motion/react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowRight,
  BarChart3,
  CheckCircle2,
  Clock3,
  Filter,
  LayoutDashboard,
  PlayCircle,
  ShieldCheck,
  Sparkles,
} from 'lucide-react';
import { Navigation } from '../Components/common/Navigation';
import { Footer } from '../Components/common/Footer';
import { SEOHead } from '../Components/common/SEOHead';
import { OrganizationSchema } from '../Components/seo/StructuredData';
import './Home.css';

const products = [
  {
    title: 'Timeline Slicer',
    label: 'Date filtering',
    text: 'A compact Power BI timeline for clear date selection without taking over the dashboard.',
    image: '/assets/timeline.png',
    logo: '/assets/timelineLogo.png',
    link: 'https://appsource.microsoft.com/en-us/product/power-bi-visuals/based-on.based_on_your_timeline?tab=Overview',
    bullets: ['Compact date range control', 'Years, quarters, months and days', 'Clean reports with more room for data'],
  },
  {
    title: 'Countdown Timer',
    label: 'Deadline tracking',
    text: 'A flexible countdown visual for launches, SLA tracking, events and operational dashboards.',
    image: '/assets/countdown.png',
    logo: '/assets/countdownLogo.png',
    link: 'https://appsource.microsoft.com/en-us/product/power-bi-visuals/based-on.based_on_your_count_down?tab=Overview',
    bullets: ['Up to 5 simultaneous timers', 'Responsive layout', 'Custom labels, colors and time units'],
  },
];

const benefits = [
  {
    icon: LayoutDashboard,
    title: 'More dashboard space',
    text: 'Compact visuals leave room for the metrics, charts and context your users actually need.',
  },
  {
    icon: ShieldCheck,
    title: 'Reliable in daily use',
    text: 'Built for practical report pages, repeated interactions and business users who need predictable tools.',
  },
  {
    icon: BarChart3,
    title: 'Easy to understand',
    text: 'Focused controls and clear defaults reduce training time and keep reports approachable.',
  },
];

const steps = [
  'Choose the visual that solves your dashboard problem.',
  'Install it from Microsoft AppSource.',
  'Configure colors, labels and behavior to match your report.',
];

export function Home() {
  const navigate = useNavigate();

  return (
    <div className="home-page">
      <SEOHead
        title="Professional Power BI Visuals & Productivity Tools"
        description="Professional Power BI custom visuals including Timeline Slicer and Countdown Timer. Space-saving, fast, and reliable solutions designed for data professionals worldwide."
        keywords="Power BI visuals, Power BI timeline slicer, Power BI countdown timer, custom Power BI visuals, Power BI dashboard tools, data visualization tools"
        ogImage="https://based-on.net/og-image.png"
        canonical="https://based-on.net/"
      />

      <OrganizationSchema
        sameAs={[
          'https://www.linkedin.com/in/iuliia-strunnikova/',
          'https://appsource.microsoft.com/en-us/marketplace/apps?search=based%20on&page=1',
        ]}
      />

      <Navigation />

      <main>
        <section className="home-hero">
          <div className="home-dot-grid home-dot-grid-left" aria-hidden="true" />
          <div className="home-dot-grid home-dot-grid-right" aria-hidden="true" />
          <div className="home-wave" aria-hidden="true" />

          <div className="home-hero-inner">
            <motion.div
              className="home-hero-copy"
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.55, ease: 'easeOut' }}
            >
              <div className="home-pill">
                <Sparkles size={15} />
                <span>Power BI visuals for cleaner dashboards</span>
              </div>
              <h1>Small visuals. Clearer reports.</h1>
              <div className="home-title-mark" />
              <p>
                BasedOn creates practical Power BI visuals that save space, keep dashboards readable,
                and help users interact with data faster.
              </p>

              <div className="home-actions">
                <button
                  className="home-button home-button-primary"
                  type="button"
                  onClick={() => document.getElementById('visuals')?.scrollIntoView({ behavior: 'smooth' })}
                >
                  <span>View visuals</span>
                  <ArrowRight size={18} />
                </button>
                <button
                  className="home-button home-button-secondary"
                  type="button"
                  onClick={() => navigate('/contact')}
                >
                  Contact support
                </button>
              </div>
            </motion.div>

            <motion.div
              className="home-hero-preview"
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.55, delay: 0.1, ease: 'easeOut' }}
              aria-label="BasedOn visual preview"
            >
              <div className="home-preview-toolbar">
                <span />
                <span />
                <span />
              </div>
              <div className="home-preview-body">
                <div className="home-preview-chart">
                  <div className="home-preview-bars" aria-hidden="true">
                    <span />
                    <span />
                    <span />
                    <span />
                    <span />
                  </div>
                  <div className="home-preview-line" aria-hidden="true" />
                </div>
                <div className="home-preview-panel">
                  <div className="home-preview-control">
                    <Filter size={18} />
                    <span>Timeline Slicer</span>
                  </div>
                  <img src="/assets/timeline.png" alt="Timeline Slicer preview" />
                </div>
              </div>
              <div className="home-preview-footer">
                <div>
                  <strong>60%</strong>
                  <span>space saved</span>
                </div>
                <div>
                  <strong>2</strong>
                  <span>visuals available</span>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        <section className="home-section home-intro">
          <div className="home-section-heading">
            <p className="home-eyebrow">What BasedOn solves</p>
            <h2>Useful visuals without dashboard clutter</h2>
            <p>
              Reports become easier to use when filters, timers and controls are compact,
              predictable and visually quiet.
            </p>
          </div>

          <div className="home-benefits">
            {benefits.map((benefit) => {
              const Icon = benefit.icon;
              return (
                <article className="home-card home-benefit-card" key={benefit.title}>
                  <div className="home-icon">
                    <Icon size={28} />
                  </div>
                  <h3>{benefit.title}</h3>
                  <p>{benefit.text}</p>
                </article>
              );
            })}
          </div>
        </section>

        <section id="visuals" className="home-section home-products">
          <div className="home-section-heading">
            <p className="home-eyebrow">Visuals</p>
            <h2>Choose the tool your dashboard needs</h2>
            <p>Two focused visuals, both designed for clarity and day-to-day reporting work.</p>
          </div>

          <div className="home-product-grid">
            {products.map((product) => (
              <article className="home-card home-product-card" key={product.title}>
                <div className="home-product-media">
                  <img src={product.image} alt={`${product.title} screenshot`} />
                </div>
                <div className="home-product-content">
                  <div className="home-product-title">
                    <img src={product.logo} alt="" aria-hidden="true" />
                    <div>
                      <p>{product.label}</p>
                      <h3>{product.title}</h3>
                    </div>
                  </div>
                  <p className="home-product-text">{product.text}</p>
                  <ul>
                    {product.bullets.map((bullet) => (
                      <li key={bullet}>
                        <CheckCircle2 size={17} />
                        <span>{bullet}</span>
                      </li>
                    ))}
                  </ul>
                  <a className="home-link" href={product.link} target="_blank" rel="noopener noreferrer">
                    <span>Open in AppSource</span>
                    <ArrowRight size={17} />
                  </a>
                </div>
              </article>
            ))}
          </div>
        </section>

        <section id="demo" className="home-section home-demo">
          <div className="home-demo-copy">
            <p className="home-eyebrow">Demo</p>
            <h2>See the Timeline Slicer in action</h2>
            <p>
              The short demo shows how the visual helps users select date ranges while keeping
              the report page compact and easy to scan.
            </p>
            <div className="home-demo-actions">
              <a
                className="home-button home-button-primary"
                href="https://youtu.be/t7iOLpI6etk?si=DsMcta62Ne18b6LM"
                target="_blank"
                rel="noopener noreferrer"
              >
                <PlayCircle size={19} />
                <span>Watch demo</span>
              </a>
              <a
                className="home-button home-button-secondary"
                href="https://appsource.microsoft.com/en-us/product/power-bi-visuals/based-on.based_on_your_timeline?tab=Overview"
                target="_blank"
                rel="noopener noreferrer"
              >
                AppSource
              </a>
            </div>
          </div>

          <div className="home-video-card">
            <iframe
              src="https://www.youtube.com/embed/t7iOLpI6etk?si=DsMcta62Ne18b6LM"
              title="Timeline Slicer demo"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              referrerPolicy="strict-origin-when-cross-origin"
              allowFullScreen
            />
          </div>
        </section>

        <section className="home-section home-process">
          <div className="home-section-heading">
            <p className="home-eyebrow">How it works</p>
            <h2>Simple path from idea to report</h2>
          </div>

          <div className="home-step-grid">
            {steps.map((step, index) => (
              <article className="home-step" key={step}>
                <span>{index + 1}</span>
                <p>{step}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="home-final">
          <div>
            <p className="home-eyebrow">Need help choosing?</p>
            <h2>Tell us what your report needs to do.</h2>
            <p>
              Share your dashboard challenge and we will point you toward the right visual
              or implementation approach.
            </p>
          </div>
          <button className="home-button home-button-primary" type="button" onClick={() => navigate('/contact')}>
            <Clock3 size={18} />
            <span>Get in touch</span>
          </button>
        </section>
      </main>

      <Footer />
    </div>
  );
}
